// Dynamic service hub route — handles all services at /services/$service
import { createFileRoute, notFound } from "@tanstack/react-router";
import type { ServiceId } from "@/data/services";
import { ServiceHubRoute } from "@/components/ServiceHubRoute";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { videoObjectNode } from "@/lib/media-schema";
import { VIDEOS } from "@/data/videos";

const SITE = "https://nyclanguageinstitute.com";


export const Route = createFileRoute("/services/$service/")({
  loader: async ({ params }) => {
    const { SERVICE_HEAD, resolveServiceIdLight } = await import("@/data/services-head.gen");
    const id = resolveServiceIdLight(params.service) as ServiceId | undefined;
    if (!id) throw notFound();
    const h = SERVICE_HEAD[id];
    return { service: id, meta: { slug: h.slug, metaTitle: h.metaTitle, metaDesc: h.metaDesc, keywords: h.keywords } };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: ctaTitle("ไม่พบบริการ", "/services/$service", "th")}] };
    const s = loaderData.meta;
    const url = `${SITE}/services/${s.slug}`;
    return {
      meta: [
        { title: ctaTitle(s.metaTitle, "/services/$service", "th")},
        { name: "description", content: ctaDesc(s.metaDesc, "/services/$service", "th")},
        { name: "keywords", content: s.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(s.metaTitle, "/services/$service", "th")},
        { property: "og:description", content: ctaDesc(s.metaDesc, "/services/$service", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/services/${s.slug}`, `/en/services/${s.slug}`),
      ],
      scripts: [
        speakableSchema(),
        ...VIDEOS.filter((v) => v.pagePath === `/services/${s.slug}`).map((v) => ({
          type: "application/ld+json",
          children: JSON.stringify(videoObjectNode({
            name: v.title,
            description: v.description,
            thumbnailUrl: v.thumbnailUrl,
            uploadDate: v.publicationDate,
            duration: v.duration,
            contentUrl: v.contentUrl,
            embedUrl: v.playerUrl,
            pageUrl: url,
          })),
        })),
      ],
    };
  },
  component: ServicePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบริการที่ค้นหา</h1>
    </main>
  ),
});

function ServicePage() {
  const { service } = Route.useLoaderData() as { service: ServiceId };
  return <ServiceHubRoute serviceId={service} />;
}

